import constate from 'constate'
import { useListEmployeePublicInfoQuery } from '@/features/employee-data/employee-data.api.ts'
import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { EmployeePublicInfo } from '@/features/employee-data/employee-data.def.ts'

export const isManager = (targetEid: string, publicInfos: { reportingManager?: string }[]) => {
  return publicInfos.some(x => !!x.reportingManager && x.reportingManager === targetEid)
}

export const [EmployeePublicInfoProvider, useEmployeePublicInfo] = constate(() => {
  const { data, isFetching, refetch } = useListEmployeePublicInfoQuery({ effectiveDate: dayjs().format('YYYY-MM-DD') }, {
    pollingInterval: 3600000, // 3600000 = 60min
    skipPollingIfUnfocused: true,
  })

  const employeeDict = useMemo(() => {
    if (!data || !data.length) return {}

    return data.reduce((acc, x) => {
      return {
        ...acc,
        [x.employeeId]: x,
      }
    }, {} as Record<string, EmployeePublicInfo>)
  }, [data])

  const userDict = useMemo(() => {
    if (!data || !data.length) return {}

    return data.reduce((acc, x) => {
      return {
        ...acc,
        [x.username]: x,
      }
    }, {} as Record<string, EmployeePublicInfo>)
  }, [data])

  const getEmployeePublicInfo = useCallback((employeeId?: string | null): EmployeePublicInfo | undefined => {
    if (!employeeId) return undefined
    return employeeDict?.[employeeId]
  }, [employeeDict])

  const getEmployeePublicInfoByUsername = useCallback((username?: string): EmployeePublicInfo | undefined => {
    if (!username) return undefined
    return userDict?.[username]
  }, [userDict])

  const checkIsManager = useCallback((employeeId: string) => {
    return isManager(employeeId, data || [])
  }, [data])

  return {
    isFetching,
    isLoading: isFetching,
    data,
    refetch,
    getEmployeePublicInfo,
    getEmployeePublicInfoByUsername,
    checkIsManager,
  }
})